import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {RecordTableAction} from "../../../../../types/record-table-action.types";
import {SortOrder} from "../../../../../types/utility.types";
import {TableColumnCheckboxService} from "../../../../records-table-container/services/table-column-checkbox.service";
import {RecordsTableService} from "../../../../../services/records-table.service";
import {RecordTableColumnParams} from "../../../../../types/records-table.types";
import {sortOrderMap} from "../../../configs/records-table-config";
import {ColumnParamsStateService} from "../../../../../services/table-services/column-params-state.service";
import {ColumnResizeService} from "../../../../../services/table-services/column-resize.service";
import {ModalStatesService} from "../../../../../services/table-services/modal-states.service";
import {TableContextService} from "../../../../../services/table-services/table-context.service";

@Component({
  selector: 'app-table-header',
  standalone: false,
  templateUrl: './table-header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: [
    '../../../records-table-view.component.scss',
    './table-header.component.scss'
  ]
})
export class TableHeaderComponent implements AfterViewInit {
  @Input() isNeedGroups: boolean = true;
  @Input({ required: true }) tableActions: Array<RecordTableAction> = [];
  @Input({ required: true }) currentKeySortParam: string = '';
  @Input({ required: true }) currentSortOrder: SortOrder = 'none';
  @Input({ required: true }) resourceParamsKey: string = '';
  @Input({ required: true }) isResizeColumn: boolean = false;
  @Input() isAnyTableActionAvailable: boolean = false;
  @Input() noEmptyTableData: boolean = false;
  @Output() sortParamsChanged: EventEmitter<string> = new EventEmitter<string>();
  @ViewChildren('headerCell') headerCells!: QueryList<ElementRef<HTMLElement>>;

  public columnParamsStateService: ColumnParamsStateService = inject(ColumnParamsStateService);
  public modalStatesService: ModalStatesService = inject(ModalStatesService);
  private tableColumnCheckBoxService: TableColumnCheckboxService = inject(TableColumnCheckboxService);
  private recordsTableService: RecordsTableService = inject(RecordsTableService);
  private columnResizeService: ColumnResizeService = inject(ColumnResizeService);
  private changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private tableContextService: TableContextService = inject(TableContextService);

  public isEditModeActive: boolean = false;

  public ngAfterViewInit() {
    this.registerHeaderCells();
    this.headerCells.changes.subscribe(() => this.registerHeaderCells());
  }

  public getChecked(id?: number | string): boolean | undefined {
    return id ?
      this.tableColumnCheckBoxService.columnCheckboxState().subtasks.find(i => i.id === id)?.completed :
      this.tableColumnCheckBoxService.columnCheckboxState().completed;
  }

  public isNeedCheckbox(index: number): boolean {
    return this.tableActions.includes('CREATE_BLOGGER_GROUPS') && index === 0;
  }

  public updateChecked(completed: boolean, id?: number | string): void {
    this.tableColumnCheckBoxService.updateColumnCheckboxState({
      id: Number(id),
      completed: completed
    });
  }

  public isColumnDisplayed(columnParams: RecordTableColumnParams): boolean {
    return this.recordsTableService.isColumnDisplayed(columnParams);
  }

  public isCurrentColumnSorted(columnKey: RecordTableColumnParams, sortOrder: SortOrder): boolean {
    if (!columnKey.sortField) { return false; }

    return this.currentKeySortParam === columnKey.sortField && this.currentSortOrder === sortOrder;
  }

  public getWidth(columnParam: RecordTableColumnParams): number {
    return this.recordsTableService.getColumnWidth(columnParam, this.tableContextService.getTableId());
  }

  public handleResizeStart(event: MouseEvent, columnParam: RecordTableColumnParams): void {
    if (this.isEditModeActive || !this.isResizeColumn) { return; }

    const target = event.target as HTMLElement;
    if (!target.classList.contains('resize-column')) { return; }

    this.columnResizeService.handleResizeStart(event, columnParam);
    this.changeDetectorRef.markForCheck();
  }

  public handleSortingChange(columnParams: RecordTableColumnParams): void {
    if (!columnParams.sortable) { return; }
    if (!columnParams.sortField) { return; }

    this.sortParamsChanged.emit(this.getSortParams(columnParams.sortField));
  }

  public handleToggleColumnStick(key: string, columnParams: RecordTableColumnParams): void {
    this.columnParamsStateService.updateSticky(key, columnParams);
  }

  private registerHeaderCells() {
    this.headerCells.forEach(ref => {
      const key = ref.nativeElement.getAttribute('data-column')!;
      this.columnResizeService.registerElement(key, ref.nativeElement);
    });
  }

  private getSortParams(columnKey: string): string {
    const isAlreadySortedByCurrentColumn = this.currentKeySortParam === columnKey;
    this.currentKeySortParam = columnKey;
    this.currentSortOrder = isAlreadySortedByCurrentColumn ?
      sortOrderMap[this.currentSortOrder] :
      'desc';

    return this.currentSortOrder === 'none' ? '' : `${columnKey},${this.currentSortOrder}`;
  }
}
